import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  InfiniteScroll,
  LoadingSpinner,
  Popover,
  PopoverContentNoPortal,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TruncatedText,
} from '@ais-ui/ui-core';
import React, { useEffect, useMemo, useRef } from 'react';
import { ShapeSelectType } from '../utils';
import { AlertTriangle, Check, ChevronsUpDown, X } from 'lucide-react';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { useInfiniteSelectShapes } from '../hooks/useInfiniteSelectShapes';
import * as Sentry from '@sentry/react';

export interface ShapeMultiSelectProps {
  value?: string[];
  onChange: (value: string[] | undefined) => void;
  allowedGeometryTypes?: string[];
}

export function ShapeMultiSelect({
  value,
  onChange,
  allowedGeometryTypes,
}: ShapeMultiSelectProps) {
  const { messages } = useLocalization();
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const { filteredFlattenedShapes, isLoading, flattenedShapes } =
    useInfiniteSelectShapes(search, allowedGeometryTypes);
  const currentSelection = Array.isArray(value) ? value : [];

  const handleUnselect = React.useCallback(
    (deselectedShapeId: string) => {
      const baseSelection = Array.isArray(value) ? value : [];
      onChange(
        baseSelection.filter((shapeId) => shapeId !== deselectedShapeId),
      );
    },
    [value, onChange],
  );

  const shapeIdToSelectMap = React.useMemo(() => {
    const map: { [id: string]: ShapeSelectType } = {};

    if (!flattenedShapes) {
      return map;
    }

    flattenedShapes.forEach((shape) => {
      if (shape.id) {
        // Make sure id is defined
        map[shape.id] = shape;
      }
    });

    return map;
  }, [flattenedShapes]);

  // Track reported missing shapes to prevent duplicate Sentry messages
  const reportedMissingIdsRef = useRef<Set<string>>(new Set());

  // Report missing shapes to Sentry (only after data has loaded, once per ID)
  useEffect(() => {
    if (isLoading || !flattenedShapes.length) return;

    const missingShapeIds = currentSelection.filter(
      (shapeId) => !shapeIdToSelectMap[shapeId]
    );

    // Find IDs that haven't been reported yet
    const newMissingIds = missingShapeIds.filter(
      (id) => !reportedMissingIdsRef.current.has(id)
    );

    if (newMissingIds.length > 0) {
      newMissingIds.forEach((id) => reportedMissingIdsRef.current.add(id));
      Sentry.captureMessage('Shape(s) not found in ShapeMultiSelect', {
        level: 'warning',
        tags: {
          component: 'ShapeMultiSelect',
          context: 'shape-not-found',
        },
        extra: {
          missingShapeIds: newMissingIds,
          totalSelected: currentSelection.length,
          availableShapesCount: flattenedShapes.length,
          allowedGeometryTypes,
        },
      });
    }

    // Clean up reported IDs that are no longer in selection
    reportedMissingIdsRef.current.forEach((id) => {
      if (!currentSelection.includes(id)) {
        reportedMissingIdsRef.current.delete(id);
      }
    });
  }, [currentSelection, shapeIdToSelectMap, isLoading, flattenedShapes.length, allowedGeometryTypes]);

  // Calculate dynamic height based on content
  const calculatedHeight = useMemo(() => {
    const COMMAND_INPUT_HEIGHT = 40;
    const COMMAND_ITEM_HEIGHT = 32;
    const LOADING_SPINNER_HEIGHT = 68;
    const COMMAND_EMPTY_HEIGHT = 68;
    const PADDING_AND_BORDERS = 10;
    const MAX_HEIGHT = 300;

    let contentHeight = COMMAND_INPUT_HEIGHT + PADDING_AND_BORDERS;

    if (isLoading) {
      // Add loading spinner height
      contentHeight += LOADING_SPINNER_HEIGHT;
    } else if (!filteredFlattenedShapes.length) {
      // Add empty state height
      contentHeight += COMMAND_EMPTY_HEIGHT;
    } else {
      // Add height for displayed shapes
      contentHeight += filteredFlattenedShapes.length * COMMAND_ITEM_HEIGHT;
    }

    return Math.min(contentHeight, MAX_HEIGHT);
  }, [filteredFlattenedShapes.length, isLoading]);

  return (
    <Popover
      open={open}
      onOpenChange={() => {
        setOpen(!open);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between pr-3 hover:bg-card gap-1 text-base shadow-sm font-normal md:text-sm"
        >
          <div className="flex flex-wrap gap-1 items-center max-w-[calc(100%-2rem)]">
            {currentSelection.length > 0 ? (
              currentSelection.map((shapeId) => {
                const shapeSelect = shapeIdToSelectMap[shapeId];

                // If shape is not found in the map, show a warning badge with tooltip
                // This can happen if the shape was deleted or doesn't match geometry type
                if (!shapeSelect) {
                  return (
                    <TooltipProvider key={shapeId}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Badge
                            variant="destructive"
                            className="max-w-[300px] cursor-help"
                          >
                            <AlertTriangle className="h-3 w-3 mr-1 flex-shrink-0" />
                            <TruncatedText
                              text={messages.shapeNotFound || 'Shape not found'}
                              className="text-left"
                            />
                            <span
                              role="button"
                              tabIndex={0}
                              className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 flex-shrink-0"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  handleUnselect(shapeId);
                                }
                              }}
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                handleUnselect(shapeId);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </span>
                          </Badge>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>ID: {shapeId}</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  );
                }

                return (
                  <Badge
                    key={shapeSelect.id}
                    variant="secondary"
                    className="max-w-[300px]"
                  >
                    <TruncatedText
                      text={shapeSelect?.name ? shapeSelect.name : ''}
                      className="text-left"
                    />
                    <span
                      role="button"
                      tabIndex={0}
                      className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 flex-shrink-0"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleUnselect(shapeId);
                        }
                      }}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleUnselect(shapeId);
                      }}
                    >
                      <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                    </span>
                  </Badge>
                );
              })
            ) : (
              <span>{messages.shapeMultiSelectText}</span>
            )}
            <span className="flex-1"></span>
          </div>
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContentNoPortal
        align="start"
        className="w-full p-0 overflow-hidden"
        style={{ height: `${calculatedHeight}px` }}
      >
        <Command shouldFilter={false} className="flex flex-col h-full">
          <CommandInput
            placeholder={messages.shapeMultiSelectPickShape}
            onValueChange={(value) => {
              setSearch(value);
            }}
            value={search}
          />
          <CommandEmpty>{messages.shapeMultiSelectNoResults}</CommandEmpty>
          {isLoading && <LoadingSpinner size="medium"></LoadingSpinner>}
          {!isLoading && (
            <InfiniteScroll
              items={filteredFlattenedShapes}
              limit={30}
              observeResize={true}
              renderItem={(shapeSelect) => {
                return (
                  <CommandItem
                    key={shapeSelect.id}
                    disabled={shapeSelect.type === 'FeatureCollection'}
                    value={shapeSelect.id}
                    onSelect={() => {
                      if (shapeSelect.id) {
                        // Toggle the selection
                        const isSelected = currentSelection.includes(
                          shapeSelect.id,
                        );
                        const newValue = isSelected
                          ? currentSelection.filter(
                              (id) => id !== shapeSelect.id,
                            )
                          : [...currentSelection, shapeSelect.id];

                        onChange(newValue.length > 0 ? newValue : []);
                        setOpen(false);
                      }
                    }}
                  >
                    <div className="flex justify-between w-full">
                      <div className="flex min-w-0 max-w-[85%]">
                        <span>
                          {new Array(shapeSelect.level + 1).join(
                            '\u00A0\u00A0\u00A0',
                          )}
                        </span>
                        <div className="px-2 flex-shrink-0">
                          {' '}
                          <shapeSelect.icon />
                        </div>
                        <TruncatedText
                          text={shapeSelect?.name ? shapeSelect.name : ''}
                          maxWidth="300px"
                        />
                      </div>
                      {shapeSelect.id && (
                        <Check
                          className={cn(
                            'mr-1 size-4 flex-shrink-0',
                            currentSelection.includes(shapeSelect.id)
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                      )}
                    </div>
                  </CommandItem>
                );
              }}
              className="flex-1 min-h-0 p-0 border rounded-lg"
            ></InfiniteScroll>
          )}
        </Command>
      </PopoverContentNoPortal>
    </Popover>
  );
}
